import React from 'react';
import PropTypes from 'prop-types';
import {
    Divider,
    FormControlLabel,
    Popover,
    Radio,
    RadioGroup,
    Typography,
} from '@mui/material';
import { ChevronDown } from 'lucide-react';

import IconButton from '../ui/IconButton';
import Menu, { MenuItem } from '../ui/Menu';
import Tooltip from '../ui/Tooltip';
import { SECURITY_IDENTITY_FIELD_OPTIONS } from '../../utils/securityIdentityPresentation';

const PICKER_TOOLTIP = 'Choose the identity field shown in this column';

function selectedIdentityOption(value) {
    return SECURITY_IDENTITY_FIELD_OPTIONS.find((option) => option.value === value)
        || SECURITY_IDENTITY_FIELD_OPTIONS[0];
}

/** The identity column's label for a field value ('Company Name' by default). */
export function securityIdentityFieldLabel(value) {
    return selectedIdentityOption(value).label;
}

// The header's picker button: a small bordered icon button (the theme's
// IconButton look) in the header's text colour.
const PickerButton = React.forwardRef(function PickerButton({ label, ...buttonProps }, ref) {
    return (
        <IconButton
            ref={ref}
            size="xs"
            label={`Choose identity field; currently showing ${label}`}
            className="!text-inherit"
            {...buttonProps}
        >
            <ChevronDown size={14} strokeWidth={2.5} aria-hidden="true" />
        </IconButton>
    );
});

PickerButton.propTypes = {
    label: PropTypes.string.isRequired,
};

/**
 * The identity column's field picker for a DataTable column: an icon button
 * opening a menu of the fields (radio items). Put the label
 * (securityIdentityFieldLabel) in the column `header` and this in
 * `meta.headerControls`, so choosing a field never sorts. `onChange(value)`
 * receives the chosen field.
 */
export function SecurityIdentityFieldMenu({ value, onChange }) {
    const selectedOption = selectedIdentityOption(value);
    return (
        <Menu
            trigger={(
                <Tooltip content={PICKER_TOOLTIP}>
                    <PickerButton label={selectedOption.label} />
                </Tooltip>
            )}
        >
            {SECURITY_IDENTITY_FIELD_OPTIONS.map((option) => (
                <MenuItem
                    key={option.value}
                    type="radio"
                    checked={option.value === selectedOption.value}
                    onSelect={() => onChange(option.value)}
                >
                    {option.label}
                </MenuItem>
            ))}
        </Menu>
    );
}

SecurityIdentityFieldMenu.propTypes = {
    value: PropTypes.string.isRequired,
    onChange: PropTypes.func.isRequired,
};

/**
 * The identity column's header in the MUI import-review tables
 * (PendingImportCard, useWatchlistReviewIdentity): the label and the picker
 * button, which opens `SecurityIdentityFieldSelector` through
 * `onOpen(buttonElement)`. The button stops the click so the header does not
 * sort.
 */
export function SecurityIdentityColumnHeader({
    value,
    selectorId,
    anchorEl,
    onOpen,
}) {
    const selectedOption = selectedIdentityOption(value);
    return (
        <span className="flex w-full min-w-0 items-center justify-between gap-0.5">
            <span className="truncate">{selectedOption.label}</span>
            <Tooltip content={PICKER_TOOLTIP}>
                <PickerButton
                    label={selectedOption.label}
                    onClick={(event) => {
                        event.stopPropagation();
                        onOpen(event.currentTarget);
                    }}
                    aria-haspopup="dialog"
                    aria-controls={anchorEl ? selectorId : undefined}
                    aria-expanded={Boolean(anchorEl)}
                />
            </Tooltip>
        </span>
    );
}

export default function SecurityIdentityFieldSelector({
    anchorEl,
    selectorId,
    value,
    onChange,
    onClose,
    ariaLabel,
    helperText = 'Select the row label below. Click the column title itself to sort it.',
    paperSx = null,
    popoverSx = null,
}) {
    return (
        <Popover
            sx={popoverSx}
            open={Boolean(anchorEl)}
            anchorEl={anchorEl}
            onClose={onClose}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            transformOrigin={{ vertical: 'top', horizontal: 'left' }}
            PaperProps={{
                id: selectorId,
                role: 'dialog',
                'aria-labelledby': `${selectorId}-title`,
                sx: paperSx ? [paperSx, { width: 250, p: 1 }] : { width: 250, p: 1 },
            }}
        >
            <Typography
                id={`${selectorId}-title`}
                variant="subtitle2"
                sx={{ fontWeight: 700, px: 0.75, py: 0.5 }}
            >
                Show in identity column
            </Typography>
            <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: 'block', px: 0.75, pb: 0.75 }}
            >
                {helperText}
            </Typography>
            <Divider sx={{ mb: 0.25 }} />
            <RadioGroup
                aria-label={ariaLabel}
                value={value}
                onChange={(event) => {
                    onChange(event.target.value);
                    onClose();
                }}
            >
                {SECURITY_IDENTITY_FIELD_OPTIONS.map((option) => (
                    <FormControlLabel
                        key={option.value}
                        value={option.value}
                        control={<Radio size="small" />}
                        label={option.label}
                        sx={{ mx: 0, '& .MuiFormControlLabel-label': { fontSize: '0.82rem' } }}
                    />
                ))}
            </RadioGroup>
        </Popover>
    );
}
